import {
  GetSubmissionResponse,
  SubmissionStatusFilter,
  useExemptionPortalCustomerSubmissionsWithinSeller,
} from '@commenda/api';
import { Badge, Button, DataTable, Icons, Tooltip } from '@commenda/kakashi';
import { TSetNotification } from '@commenda/types/src/random';
import {
  cn,
  ExemptionPortalHelper,
  ExemptionPortalStatusOptions,
} from '@commenda/utils';
import { useRouter } from 'next/router';
import { useState } from 'react';
import {
  ISerializedCertificate,
  serializeCertificates,
} from '../utils/certificateSerializer';
import ViewCertificateModal from '../v2/modal/ViewCertificateModal';

const ExemptionList = ({
  corporationId,
  customerId,
  setNotification,
}: {
  corporationId: number;
  customerId: string;
  setNotification: TSetNotification;
}) => {
  const router = useRouter();

  const { submissions, isLoading, error, mutate } =
    useExemptionPortalCustomerSubmissionsWithinSeller(
      corporationId,
      customerId
    );

  const [selectedStatus, setSelectedStatus] =
    useState<ExemptionPortalStatusOptions>(SubmissionStatusFilter.APPROVED);
  const [selectedCertificate, setSelectedCertificate] =
    useState<GetSubmissionResponse | null>(null);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const statusTabs = [
    {
      value: SubmissionStatusFilter.APPROVED,
      label: 'Approved',
      count: ExemptionPortalHelper.getExemptionCertificateCountForStatus(
        submissions,
        SubmissionStatusFilter.APPROVED
      ),
    },
    {
      value: SubmissionStatusFilter.UNDER_REVIEW,
      label: 'Under Review',
      count: ExemptionPortalHelper.getExemptionCertificateCountForStatus(
        submissions,
        SubmissionStatusFilter.UNDER_REVIEW
      ),
    },
    {
      value: SubmissionStatusFilter.EXPIRING_SOON,
      label: 'Expiring soon',
      count: ExemptionPortalHelper.getExemptionCertificateCountForStatus(
        submissions,
        SubmissionStatusFilter.EXPIRING_SOON
      ),
    },
    {
      value: SubmissionStatusFilter.EXPIRED,
      label: 'Expired',
      count: ExemptionPortalHelper.getExemptionCertificateCountForStatus(
        submissions,
        SubmissionStatusFilter.EXPIRED
      ),
    },
    {
      value: SubmissionStatusFilter.REJECTED,
      label: 'Rejected',
      count: ExemptionPortalHelper.getExemptionCertificateCountForStatus(
        submissions,
        SubmissionStatusFilter.REJECTED
      ),
    },
  ];

  const onClickAddNewCertificate = () => {
    router.push(
      `/sales-tax/exemption-certificate/new?corporationId=${corporationId}&customerId=${customerId}`
    );
  };

  const columnGroups = [
    {
      id: 'certificateInfo',
      columns: [
        {
          id: 'stateNames',
          accessorKey: 'stateNames',
          header: 'States',
          width: 350,
          cell: ({ row }) => (
            <div className="flex gap-2">
              {row.original.stateNames.slice(0, 2).map((stateName) => (
                <Badge key={stateName} variant="gray" size="sm">
                  {stateName}
                </Badge>
              ))}
              {row.original.stateNames.length > 2 && (
                <Tooltip
                  content={row.original.stateNames.slice(2).join(', ')}
                  side="right"
                >
                  <Badge variant="gray" size="sm">
                    +{row.original.stateNames.length - 2} more
                  </Badge>
                </Tooltip>
              )}
            </div>
          ),
        },

        {
          id: 'certificateTypeDisplay',
          accessorKey: 'certificateTypeDisplay',
          header: 'Certificate type',

          cell: ({ row }) => (
            <Badge variant="gray" size="sm">
              {row.original.certificateTypeDisplay}
            </Badge>
          ),
        },

        {
          id: 'exemptionReasonDisplay',
          accessorKey: 'exemptionReasonDisplay',
          header: 'Exemption reason',
          cell: ({ row }) => (
            <span className="txt-small-regular">
              {row.original.exemptionReasonDisplay}
            </span>
          ),
        },
        {
          id: 'statusDisplay',
          accessorKey: 'statusDisplay',
          header: 'Review status',
          cell: ({ row }) => (
            <Badge variant={row.original.statusBadgeVariant} size="sm">
              {row.original.statusDisplay}
            </Badge>
          ),
        },
        {
          id: 'expirationDateDisplay',
          accessorKey: 'expirationDateDisplay',
          header: 'Expiration date',
          cell: ({ row }) => {
            const expirationDate = row.original.expirationDateDisplay;

            if (!expirationDate) {
              return <span className="txt-small-regular">Doesn't expire</span>;
            }

            return (
              <span className="txt-small-regular">
                {row.original.expirationDateDisplay}
              </span>
            );
          },
        },
        {
          id: 'submittedDate',
          accessorKey: 'submittedDate',
          header: 'Submitted on',
          cell: ({ row }) => (
            <span className="txt-small-regular">
              {row.original.submittedDate}
            </span>
          ),
        },
      ],
    },
  ];

  const serializedCertificates = serializeCertificates(
    ExemptionPortalHelper.getFilteredExemptionCertificates(
      submissions,
      selectedStatus
    ) ?? []
  );

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex items-center justify-between">
        <div className="txt-extra-large-bold text-text-secondary flex items-center gap-2">
          Exemption Certificates
        </div>
        <Button
          size="sm"
          onClick={onClickAddNewCertificate}
          variant="secondary"
        >
          <Icons.Plus className="size-4" />
          Add exemption
        </Button>
      </div>
      <div className="grid grid-cols-5">
        {statusTabs.map((tab, index) => (
          <div
            key={tab.value}
            onClick={() =>
              setSelectedStatus(tab.value as SubmissionStatusFilter)
            }
            className={cn(
              'p-2xl flex items-center justify-between txt-small-medium border border-primary-200 rounded-none cursor-pointer',
              index === 0 && 'rounded-l-md',
              index === statusTabs.length - 1 && 'rounded-r-md',
              selectedStatus === tab.value
                ? 'text-text-primary bg-bg-primary-hover'
                : 'text-text-tertiary',
              index !== 0 && 'border-l-0'
            )}
          >
            <span>{tab.label}</span>
            <span>{tab.count}</span>
          </div>
        ))}
      </div>

      <DataTable
        data={serializedCertificates}
        columnGroups={columnGroups}
        tableCellClassName="txt-small-regular text-text-secondary"
        emptyStateConfig={{
          title: 'No certificates found',
          description: 'You have no certificates to display',
        }}
        columnSelectorConfig={{
          isEnabled: true,
          placeholder: 'Customise columns',
          searchText: 'Search columns',
          notFoundText: 'No columns found',
          selectAllText: 'Show all columns',
        }}
        searchConfig={{
          isEnabled: true,
          placeholder: 'Search customer, state, exemption reason',
          columnsToEnableSearchFor: [
            'customerName',
            'stateName',
            'exemptionReasonDisplay',
          ],
        }}
        paginationConfig={{
          isEnabled: true,
          pageSize: 20,
          type: 'PAGE',
        }}
        tableViewConfig={{
          isEnabled: false,
          defaultView: 'expanded',
        }}
        onRowClick={(row: ISerializedCertificate) =>
          setSelectedCertificate(row.originalSubmission)
        }
      />

      {selectedCertificate && (
        <ViewCertificateModal
          isShown={!!selectedCertificate}
          onClose={() => setSelectedCertificate(null)}
          certificate={selectedCertificate}
          onUpdate={() => {}}
        />
      )}
    </div>
  );
};

export default ExemptionList;
